import { useState, useEffect, useRef } from 'react'
import { useApp } from '../context/AppContext'
import NotificationSound from './../sound/notification-sound.mp3'

/**
 * Reproduce el sonido de notificación configurando el volumen.
 * Maneja las restricciones de reproducción automática del navegador.
 * @function
 */
const playNotificationSound = () => {
  const audio = new Audio(NotificationSound)

  // Ajustamos el volumen para mantener la "suavidad" (rango 0.0 a 1.0)
  // 0.15 es un susurro elegante; súbelo a 0.3 si el archivo original es muy silencioso.
  audio.volume = 1

  audio.play().catch((err) => {
    // El navegador puede bloquear el audio si no ha habido interacción previa del usuario
    console.warn(
      'La reproducción automática fue bloqueada o el archivo no existe:',
      err
    )
  })
}

/**
 * Configuración de estilos e iconos según el tipo de notificación.
 * @type {Object.<string, {icon: string, color: string, bg: string, border: string}>}
 */
const typeConfig = {
  warning: {
    icon: 'fa-triangle-exclamation',
    color: '#f59e0b',
    bg: '#fffbeb',
    border: '#fcd34d',
  },
  info: {
    icon: 'fa-circle-info',
    color: '#3b82f6',
    bg: '#eff6ff',
    border: '#93c5fd',
  },
  success: {
    icon: 'fa-circle-check',
    color: '#10b981',
    bg: '#ecfdf5',
    border: '#6ee7b7',
  },
  error: {
    icon: 'fa-circle-xmark',
    color: '#ef4444',
    bg: '#fef2f2',
    border: '#fca5a5',
  },
}

/**
 * Estado global para controlar si los estilos CSS ya fueron inyectados en el DOM.
 * @type {boolean}
 */
let stylesInjected = false

/**
 * Inyecta las animaciones necesarias para el Toast en el head del documento.
 * Solo se ejecuta una vez.
 * @function
 */
function injectToastStyles() {
  if (stylesInjected) return
  stylesInjected = true

  const style = document.createElement('style')
  style.textContent = `
    @keyframes notif-slide-in {
      from { transform: translateX(110%); opacity: 0; }
      to   { transform: translateX(0);    opacity: 1; }
    }
    @keyframes notif-slide-out {
      from { transform: translateX(0);    opacity: 1; }
      to   { transform: translateX(110%); opacity: 0; }
    }
    .notif-toast-enter { animation: notif-slide-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
    .notif-toast-exit  { animation: notif-slide-out 0.3s ease forwards; }
  `
  document.head.appendChild(style)
}

/**
 * Crea y muestra un Toast flotante dinámicamente en el DOM.
 * @function
 * @param {Object} notification - Objeto de notificación.
 * @param {string} notification.type - Tipo de notificación (success, error, info, warning).
 * @param {string} notification.title - Título de la notificación.
 * @param {string} [notification.body] - Cuerpo o mensaje de la notificación.
 */
function showToast(notification) {
  injectToastStyles()

  const cfg = typeConfig[notification.type] ?? typeConfig.info
  const isDark =
    document.documentElement.getAttribute('data-bs-theme') === 'dark'

  const toast = document.createElement('div')
  toast.className = 'notif-toast-enter'
  toast.style.cssText = `
    position: fixed;
    top: 72px;
    right: 20px;
    z-index: 9999999;
    width: 320px;
    background: ${isDark ? '#1e293b' : cfg.bg};
    border: 1px solid ${isDark ? '#334155' : cfg.border};
    border-left: 4px solid ${cfg.color};
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    box-shadow: 0 8px 24px rgba(0,0,0,${isDark ? '0.4' : '0.1'});
    cursor: pointer;
    font-family: inherit;
  `

  toast.innerHTML = `
    <div style="
      width: 34px; height: 34px; min-width: 34px;
      border-radius: 50%;
      background: ${cfg.color}22;
      display: flex; align-items: center; justify-content: center;
    ">
      <i class="fa-solid ${cfg.icon}" style="color: ${cfg.color}; font-size: 0.85rem;"></i>
    </div>
    <div style="flex: 1; min-width: 0;">
      <div style="
        font-weight: 600;
        font-size: 0.85rem;
        color: ${isDark ? '#f1f5f9' : '#1e293b'};
        margin-bottom: 3px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      ">${notification.title}</div>
      <div style="
        font-size: 0.78rem;
        color: ${isDark ? '#94a3b8' : '#64748b'};
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      ">${notification.body ?? ''}</div>
    </div>
    <button style="
      background: none; border: none; cursor: pointer;
      color: ${isDark ? '#64748b' : '#94a3b8'};
      padding: 0; font-size: 0.8rem; line-height: 1;
      flex-shrink: 0;
    ">✕</button>
  `

  const dismiss = () => {
    toast.className = 'notif-toast-exit'
    toast.addEventListener('animationend', () => toast.remove(), { once: true })
  }

  toast.addEventListener('click', dismiss)
  document.body.appendChild(toast)

  // Auto-dismiss a los 4 segundos
  setTimeout(dismiss, 4000)
}

/**
 * Hook para gestionar notificaciones en tiempo real mediante Supabase.
 * @hook
 * @returns {Object} Un objeto con:
 * - notifications: Lista de notificaciones actuales.
 * - unreadCount: Cantidad de notificaciones sin leer.
 * - markAsRead: Función para marcar una notificación como leída.
 * - markAllAsRead: Función para marcar todas las notificaciones como leídas.
 * - loading: Estado de carga inicial.
 */
export default function useNotifications() {
  const { supabase, user } = useApp()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const channelRef = useRef(null)
  const isFirstLoad = useRef(true)

  useEffect(() => {
    if (!user?.id) return

    fetchNotifications()
    subscribeRealtime(user.id)

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [user?.id])

  /**
   * Obtiene las últimas 15 notificaciones del usuario desde la base de datos.
   * @async
   */
  async function fetchNotifications() {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .schema('Tickets')
        .from('notification')
        .select('*')
        .eq('userId', user?.id)
        .order('createdAt', { ascending: false })
        .limit(15)

      if (error) throw error
      setNotifications(data || [])
    } catch (err) {
      console.error('Error fetching notifications:', err)
    } finally {
      setLoading(false)
      isFirstLoad.current = false
    }
  }

  /**
   * Configura la suscripción en tiempo real a los cambios en la tabla de notificaciones.
   * @param {string|number} userId - ID del usuario actual.
   */
  function subscribeRealtime(userId) {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
    }

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'Tickets',
          table: 'notification',
          filter: `userId=eq.${userId}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new, ...prev])

          // Solo dispara sonido y toast si no es la carga inicial
          if (!isFirstLoad.current) {
            playNotificationSound()
            showToast(payload.new)
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'Tickets',
          table: 'notification',
          filter: `userId=eq.${userId}`,
        },
        (payload) => {
          setNotifications((prev) =>
            prev.map((n) => (n.id === payload.new.id ? payload.new : n))
          )
        }
      )
      .subscribe()

    channelRef.current = channel
  }

  /**
   * Actualiza el estado de una notificación a "leída" localmente y en la base de datos.
   * @async
   * @param {number|string} id - ID de la notificación.
   */
  const markAsRead = async (id) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, readAt: new Date().toISOString() } : n
      )
    )
    try {
      const { error } = await supabase
        .schema('Tickets')
        .from('notification')
        .update({ readAt: new Date().toISOString() })
        .eq('id', id)

      if (error) throw error
    } catch (err) {
      console.error('Error marking as read:', err)
      fetchNotifications()
    }
  }

  /**
   * Marca todas las notificaciones no leídas como leídas de forma masiva.
   * @async
   */
  const markAllAsRead = async () => {
    const unreadIds = notifications.filter((n) => !n.readAt).map((n) => n.id)
    if (unreadIds.length === 0) return

    const now = new Date().toISOString()

    setNotifications((prev) =>
      prev.map((n) => (!n.readAt ? { ...n, readAt: now } : n))
    )

    try {
      const { error } = await supabase
        .schema('Tickets')
        .from('notification')
        .update({ readAt: now })
        .in('id', unreadIds)

      if (error) throw error
    } catch (err) {
      console.error('Error marking all as read:', err)
      fetchNotifications()
    }
  }

  return {
    notifications,
    unreadCount: notifications.filter((n) => !n.readAt).length,
    markAsRead,
    markAllAsRead,
    loading,
  }
}
